import type { RoleName, ThemeMode } from "@app/core";
import { create } from "zustand";

import type { EntityTarget } from "./theme.store.js";

/** What the palette board paints when clicked — any color-editable thing on a
 *  page hosting the board (theme + organize settings). Serializable so the
 *  board can resolve label/current color/apply action from it. The `label`
 *  kind writes to the label RECORD (labels.store.setColor), not the theme. */
export type BoardTargetRef =
  | { kind: "entity"; target: EntityTarget }
  | { kind: "label"; id: string }
  | { kind: "paletteSlot"; index: number }
  | { kind: "role"; mode: ThemeMode; role: RoleName }
  | { kind: "source"; mode: ThemeMode };

interface PaletteBoardState {
  /** The currently armed target; board clicks apply to it. */
  target: BoardTargetRef | null;
  arm: (target: BoardTargetRef) => void;
}

/** Page-scoped UI state: which color target the palette board paints. Not
 *  persisted — arming resets when the theme settings page remounts. */
export const usePaletteBoardStore = create<PaletteBoardState>((set) => ({
  target: null,
  arm: (target) => set({ target }),
}));

function entityTargetKey(target: EntityTarget): string {
  switch (target.kind) {
    case "global": return "global";
    case "taskKind": return `kind:${target.isEvent ? "event" : "task"}`;
    case "priority": return `priority:${target.priority}`;
    case "calendar": return `calendar:${target.id}`;
    case "topic": return `topic:${target.id}`;
  }
}

/** Stable identity of a board target — keys `entity.boardSamples` and answers
 *  "is this row the armed one". */
export function boardTargetKey(ref: BoardTargetRef): string {
  switch (ref.kind) {
    case "entity": return entityTargetKey(ref.target);
    case "label": return `label:${ref.id}`;
    case "paletteSlot": return `slot:${ref.index}`;
    case "role": return `role:${ref.mode}:${ref.role}`;
    case "source": return `source:${ref.mode}`;
  }
}
